import { computed, Ref, ref, watch } from "@vue/composition-api";
import axios from "axios";
import moment from "moment";
import {
  AppTheme,
  AppFeature,
  Env,
  isEditionHighEnough,
  FeatureId,
  FeatureGroupId,
  ServiceHelper,
  LoggedUser,
} from "../models";
import { getQueryParams, removeQueryParams } from "@/utils/url";
import * as api from "@/common/api/api";
import {
  delayBy,
  groupByObj,
  isNotEmpty,
  normalize_booleans,
  once,
  remove_direct_key,
} from "@/fp";
import AuditlogModal from "@/components/modals/AuditlogModal.vue";
import { TimeAxisType } from "@/gql/types";
import { UseLoading } from "../composition/loading";
import {
  AppThemeStorage,
  PartnerIdStorage,
  RegionUrlStorage,
  UserStorage,
} from "@/storage";
import { state } from "./state";
import * as login from "./login";
import * as insights from "./insights/insights";
import * as infotypes from "./infotypes";
import * as timeFrame from "./timeframe";
import * as apps from "./apps/apps";
import cms from "./cms/cms.state";
import { notifyMipSuccess, sendMipConsentCode } from "../api/integrations.api";
import { UseModal } from "@/components/root/modal";
import PlansModal from "@/components/modals/plans-modal/PlansModal.vue";
import PremiumModal from "@/components/premium/PremiumModal.vue";
import { UserSettings, user_theme } from "../api/user-settings.api";
import { getTotalAccountsCount } from "@/views/accounts/data/api";
import { get_orgs_employees_and_departments } from "./org-scope/org-scope";
import VueRouter from "vue-router";
import { investigation } from "./other-modules-delegation.state";
import { CMS } from "./cms/cms.api";
import OrgAlertMsgsModalVue from "@/components/modals/OrgAlertMsgsModal.vue";
import WalkthroughModalVue from "@/components/modals/walkthrough-modal/WalkthroughModal.vue";
import PaymentReminderModalVue from "@/components/modals/PaymentReminderModal.vue";
import { UserStatus } from "@/views/admins/data/models";

const { getter, action, mutation, m, setOnReady } = state;

const PAYMENT_REMINDER_DOMAINS = ["calgaryeconomicdevelopment.com"];
let paymentReminderDisplayed = false;

const extractDomain = (email?: string) => {
  if (!email) {
    return "";
  }
  return email.split("@").pop()?.toLowerCase() ?? "";
};

const shouldShowPaymentReminder = (email?: string) => {
  return false;
  //const domain = extractDomain(email);
  //return !!domain && PAYMENT_REMINDER_DOMAINS.includes(domain);
};

const showPaymentReminderIfNeeded = async (email?: string) => {
  if (!shouldShowPaymentReminder(email) || paymentReminderDisplayed) {
    return;
  }
  paymentReminderDisplayed = true;
  await UseModal().show(PaymentReminderModalVue);
};

const resetPaymentReminderModal = () => {
  paymentReminderDisplayed = false;
};

//#region  === ENV ===
const is_local_dev = location.host === "localhost:8080";
const is_preview = getter((s) => s.env.mock);
const is_not_preview = getter((s) => !s.env.mock);

const isDemo = getter((s) => {
  const { name, orgName } = s.user;
  const notUs = name !== "x2oadmin@cognni.ai";
  const isProbot = orgName.search("probotai") > -1;
  return notUs && isProbot;
});

const is_org_suspended = ref(false);

const env = getter((s) => s.env);
const _setEnv = m.set("env");
// @ts-ignore
window._setEnv = _setEnv;
const setEnvUsingRegionUrl = mutation<string>(
  "set env using region url",
  (s, regionUrl) => {
    return {
      ...s,
      env: {
        ...s.env,
        analytics: regionUrl + "/analytic",
        gql: regionUrl + "/intelligence/data/graphql",
        server: regionUrl,
      },
    };
  },
);
const handle_env = action<Env>("get env", async ({ mutate }, env) => {
  const regionUrl = RegionUrlStorage.get();
  mutate(_setEnv, env);
  if (regionUrl) {
    mutate(setEnvUsingRegionUrl, regionUrl);
  }
  axios.defaults.baseURL = regionUrl || env.server;
  if (location.host === "localhost:8080") {
    return;
  }
  if (["production", "uat"].includes(env.env)) {
    //@ts-ignore
    window.__sysLog = console.log;
    //@ts-ignore
    window.console.log = () => {
      /*  */
    };
    //@ts-ignore
    window.__sysRestoreLog = () => {
      //@ts-ignore
      console.log = window.__sysLog;
    };
  }
});
// [Microsoft , Auditlog , Gmail] -> [onedrive, sharepoint ,exchange, teams,gmail]
const data_sources = getter((s) =>
  s.user.dataSourceApplications.map((source) =>
    ServiceHelper.as_dropdown_item(source),
  ),
);

const ms_sources = {
  onedrive: true,
  sharepoint: true,
  exchange: true,
  exchange_365: true,
  teams: true,
  ms_teams: true,
};

const data_sources_includes_ms = getter((s) =>
  s.user.dataSourceApplications.some((source) => source in ms_sources),
);

const ms_data_sources = getter((s) =>
  s.user.dataSourceApplications.filter((source) => source in ms_sources),
);

const data_sources_contain_only_gmail = getter((s) => {
  const services = s.user.dataSourceApplications;
  return services.length == 1 && services[0] === "gmail";
});
const paymentEdition = getter((s) => s.user.paymentEdition);
//#endregion

// === BASED ON ===
const isLoadingBasedOn = getter((s) => s.isLoadingBasedOn);
const setIsLoadingBasedOn = m.set("isLoadingBasedOn");
const basedOn = getter((s) => s.basedOn);
const setBasedOn = m.set("basedOn");

const { isLoading: isLoadingTimeFrame, setLoading: setLoadingTimeFrame } =
  UseLoading(false);
const loadReportTimeFrame = action<void>(
  "load report time frame",
  async ({ mutate }) => {
    setLoadingTimeFrame(true);
    const res = await api.getReportTime();
    if (res.success) {
      const { from, to } = res.value;
      mutate(setBasedOn, {
        ...basedOn.value,
        dateFrom: from,
        dateTo: to,
      });
      mutate(timeFrame.setTimeFrameOptions, { from, to });
    }
    setLoadingTimeFrame(false);
  },
);

const _data_available_from = ref("");
//@ts-ignore
window._data_available_from = _data_available_from;
const is_data_available = computed(() => {
  if (timeFrame.selectedTimeFrameOption.value) {
    const time = moment(_data_available_from.value, "YYYY-MM-DD");
    return time.isBefore(
      moment(timeFrame.selectedTimeFrameOption.value.from, "YYYY-MM-DD"),
    );
  } else {
    return true;
  }
});

// date formatted as YYYY-MM-DD
const is_before_date_of_new_piis = (date: string) =>
  moment(date, "YYYY-MM-DD").isBefore("2025-05-01");

watch(
  () => timeFrame.selectedTimeFrameOption.value,
  (new_val, old_val) => {
    if (new_val && old_val && new_val.from !== old_val.from) {
      const is_old_before_date_of_new_piis = is_before_date_of_new_piis(
        old_val.from,
      );
      const is_new_before_date_of_new_piis = is_before_date_of_new_piis(
        new_val.from,
      );
      if (is_old_before_date_of_new_piis != is_new_before_date_of_new_piis) {
        console.log("INSIDE WATCHER ", is_new_before_date_of_new_piis);
        infotypes.loadInfoTypes({
          is_before_date_of_new_piis: is_new_before_date_of_new_piis,
        });
      }
    }
  },
);

const features: Ref<{ [key: string]: AppFeature }> = ref({});
const features_by_group = computed(() =>
  groupByObj<AppFeature, "group">("group", Object.values(features.value)),
);
const toggle_feature = (feature_id: string, is_on: boolean) => {
  features.value = {
    ...features.value,
    [feature_id]: {
      ...features.value[feature_id],
      enabled: is_on,
    },
  };
  // Persist so preview/local environments survive page reloads.
  try {
    const stored = JSON.parse(
      localStorage.getItem("sldx_feature_overrides") || "{}",
    );
    stored[feature_id] = is_on;
    localStorage.setItem("sldx_feature_overrides", JSON.stringify(stored));
  } catch (_) {
    /* ignore storage errors in private/restricted contexts */
  }
};

const FeatureHelper = {
  is_enabled: (id: FeatureId) => {
    return isNotEmpty(features.value[id]) && features.value[id].enabled;
  },
  is_some_of_group_enabled: (group_id: FeatureGroupId): boolean => {
    return (
      features_by_group.value[group_id] &&
      features_by_group.value[group_id].some((d) => d.enabled)
    );
  },
};

const user_preferences: Ref<{ [key: string]: any }> = ref({});
const update_user_preference = async (key: string, value: any) => {
  return await api.update_preferences({
    ...user_preferences.value,
    [key]: value,
  });
};

const remove_user_preference = async (key: string) => {
  user_preferences.value = remove_direct_key(key, user_preferences.value);
  return await api.update_preferences({
    ...user_preferences.value,
  });
};

const in_motion_services_flags = ref<{
  is_ms_org_only: boolean;
  is_ms_and_google_org: boolean;
  is_google_org_only: boolean;
}>({
  is_ms_org_only: false,
  is_ms_and_google_org: false,
  is_google_org_only: false,
});

const loadUserStatus = async (): Promise<
  | {
      success: true;
      isActive: boolean;
      isOrgSuspended: boolean;
    }
  | { success: false }
> => {
  const result = await api.getUserStatus();
  if (result.success) {
    _data_available_from.value = result.value.dataAvailableFrom;
    if (
      result.value.in_motion.is_ms_org_only ||
      result.value.in_motion.is_ms_and_google_org
    ) {
      result.value.user.dataSourceApplications.push("gmail", "gdrive", "gchat");
    }
    const u: LoggedUser = {
      ...login.loggedUser.value,
      ...result.value.user,
      authorization: login.loggedUser.value.authorization,
    };
    PartnerIdStorage.save(result.value.user.partnerId);

    const { features: _features, preferences } = result.value;
    features.value = normalize_booleans(_features);
    user_preferences.value = preferences;

    in_motion_services_flags.value = result.value.in_motion;

    login.setLoggedUser(u);
    void showPaymentReminderIfNeeded(u.email);
    UserStorage.save(u);
    if (env.value.type === "Regular") {
      if (!result.value.user.auditLogConsent && result.value.user.showUI) {
        if (!result.value.isOrgSuspended) {
          const { UseModal } = await import(
            "@/components/root/modal/modal.state"
          );
          UseModal().show(AuditlogModal);
        }
      }
    }
    if (result.value.user.showUI && result.value.isActive) {
      await Promise.all([
        loadReportTimeFrame(),
        infotypes.loadInfoTypes({
          is_before_date_of_new_piis: is_before_date_of_new_piis(
            timeFrame.selectedTimeFrameOption.value.from,
          ),
        }),
        insights.loadInsights(),
      ]);

      const latestDate = moment(basedOn.value.dateTo);
      const opt = timeFrame.timeFrameOptions.value
        .filter((d) => d.type === TimeAxisType.Month)
        .find((opt) => latestDate.isAfter(opt.from));
      if (opt) {
        timeFrame.setSelectedTimeFrameOption(opt);
      }
    }
    org_alert_msg_flow(result.value.user.orgName);
    return {
      success: true,
      isActive: result.value.isActive,
      isOrgSuspended: result.value.isOrgSuspended,
    };
  }
  return {
    success: false,
  };
};

const org_alert_msg_flow = async (org_name: string) => {
  const org_alert_res = await CMS.get_org_alter_msg(org_name);
  if (
    org_alert_res.success &&
    org_alert_res.value[0] &&
    org_alert_res.value[0].display
  ) {
    UseModal().show(OrgAlertMsgsModalVue, org_alert_res.value[0]);
  }
};

const _isInit = ref(false);

const forceInit = () => {
  _isInit.value = true;
};

const isOrgActive = getter((s) => s.isOrgActive);
const setIsOrgActive = m.set("isOrgActive");

const before_init_app = once(
  action<Env>("before init app", async ({ dispatch, mutate }, env) => {
    await dispatch(handle_env, env);
    const params = getQueryParams();
    if (params.regionUrl) {
      const success = RegionUrlStorage.save(params.regionUrl);
      if (success) {
        axios.defaults.baseURL = params.regionUrl;
        mutate(setEnvUsingRegionUrl, params.regionUrl as string);
      }
    }

    await dispatch(login.login, params);

    if (
      params.code &&
      location.pathname.search("data-sources-management") === -1
    ) {
      switch (params.state) {
        case "mip":
          const res = await sendMipConsentCode(params.code);
          if (res.success) {
            await notifyMipSuccess();

            import("@/router/index").then(({ default: router }) => {
              router().replace({
                name: "MIP",
                query: { stage: "3" },
              });
            });
          }
          break;
        case "auditlog":
          await dispatch(login.auditLogConcent, params.code);
          break;
        default:
          break;
      }
      await delayBy(500);
    }
  }),
);

//#region Departments  Employees & Admins

const _departments: Ref<Record<string, string>> = ref({});
const departments = computed(() => _departments.value);

const _departments_people_count: Ref<Record<string, number>> = ref({});
const departments_people_count = computed(
  () => _departments_people_count.value,
);

const _employees: Ref<Record<string, string>> = ref({});
const employees = computed(() => _employees.value);
const _employees_full_objects: Ref<
  Record<
    string,
    {
      email: string;
      name: string;
      dep_id: string;
      dep_name: string;
    }
  >
> = ref({});
const employees_full_objects = computed(() => _employees_full_objects.value);

const get_employee_department_id = (emp_id: string) =>
  employees.value[emp_id] || "";

const load_departments_employees_and_admins = async (is_mock: boolean) => {
  const {
    departments,
    employees,
    employees_full,
    employees_count_per_department,
  } = await get_orgs_employees_and_departments(is_mock);
  _departments.value = departments;
  _departments_people_count.value = employees_count_per_department;
  _employees.value = employees;
  _employees_full_objects.value = employees_full;

  const admin_res = await api.get_indexed_admins();
  if (admin_res.success) {
    const admins_only_by_email = Object.entries(admin_res.value).reduce(
      (acc, [k, v]) => {
        acc[k] = v.email;
        return acc;
      },
      {} as Record<string, string>,
    );
    _admins_with_status.value = admin_res.value;
    _admins.value = {
      ...admins_only_by_email,
      [COGNNI_ADMIN_ID]: "system@congnni.ai",
    };
  }
};

const _admins: Ref<Record<string, string>> = ref({});
const _admins_with_status: Ref<
  Record<string, { email: string; status: UserStatus }>
> = ref({});
const COGNNI_ADMIN_ID = "00000000-0000-0000-0000-00000000000f";
const admins = computed(() => _admins.value);
const admins_with_status = computed(() => _admins_with_status.value);

const get_employee_department_name = (emp_id: string) => {
  const id = get_employee_department_id(emp_id);
  return id ? departments.value[id] : "";
};
// @ts-ignore
window.employees = employees;
// @ts-ignore
window.employees_full_objects = employees_full_objects;
// @ts-ignore
window.departments = departments;
// @ts-ignore
window.admins = admins;
//#endregion

const sessions_query_params = ref({});

const init = action<Env>("init shared state", async ({ mutate }, env) => {
  if (_isInit.value) {
    return;
  }
  const theme_from_storage = AppThemeStorage.get();
  if (theme_from_storage) {
    mutate(set_theme, theme_from_storage as AppTheme);
  }

  const user_status_res = await loadUserStatus();

  if (user_status_res.success) {
    mutate(setIsOrgActive, user_status_res.isActive);
    if (user_status_res.isOrgSuspended) {
      is_org_suspended.value = true;
      // TODO:
      // cancel all API request , block all routes , show UI for suspended state.
      return;
    }
  }

  if (
    !user_status_res.success ||
    !user_status_res.isActive ||
    login.loggedUser.value.isProcessingOrg
  ) {
    _isInit.value = false;
    return;
  }

  api.getAnonymousUserId().then((resId) => {
    if (resId.success) {
      mutate(setAnonymousUserId, resId.value);
    }
  });

  sessions_query_params.value = getQueryParams();

  const { state: data_sources_state } = await import(
    "@/views/customize/data-sources/data/state"
  );

  const { state: playbooks_sources_state } = await import(
    "@/views/playbooks/data/state"
  );

  await Promise.all([
    cms.loadCmsData(),
    apps.loadApps(),
    load_departments_employees_and_admins(env.mock),
    data_sources_state.init(),
    playbooks_sources_state.init(),
  ]);

  UserSettings.init(login.loggedUser.value.email);
  const theme_color = await user_theme.get();
  mutate(set_theme, theme_color);
  _isInit.value = true;
  /*  if (
    SharedState.is_google_forged.value &&
    !SharedState.loggedUser.value.services.includes("Google")
  ) {
    UseModal().show(WalkthroughModalVue, {
      id: "gsuite-setup",
      use_as_mask: true,
    });
  } */

  removeQueryParams();
});

//#region events
const { isLoading: isLoadingEvent, setLoading: setLoadingEvent } =
  UseLoading(false);

const currentEvent = getter((s) => s.events[s.currentEventId]);

//#endregion

const onReady = setOnReady(_isInit);

const anonymousUserId = getter((s) => s.anonymousUserId);
const setAnonymousUserId = m.set("anonymousUserId");

//#region Theme
const theme = getter((s) => s.theme);
const _toggleTheme = mutation<void>("toggle theme", (s) => {
  return {
    ...s,
    theme: s.theme === "light" ? "dark" : "light",
  };
});
const toggleTheme = action<void>("toggle theme", async ({ mutate, state }) => {
  const toTheme = state.theme === "light" ? "dark" : "light";
  user_theme.update(toTheme);
  AppThemeStorage.save(toTheme);
  // @ts-ignore
  window.changeTheme(toTheme);
  // @ts-ignore
  mutate(_toggleTheme);
});

const change_theme_locally = action<AppTheme>(
  "change theme locally",
  async ({ mutate }, val) => {
    mutate(set_theme, val);
  },
);

const set_theme = mutation<AppTheme>("set theme", (s, theme) => {
  // @ts-ignore
  window.changeTheme(theme);
  return {
    ...s,
    theme,
  };
});
//#endregion

const ifIsPayingUser = (
  cb: () => void,
  modal_options?: {
    premium_modal_key?: string;
    title?: string;
    postModalExec?: () => void;
  },
) => {
  if (
    isEditionHighEnough({
      userEdition: SharedState.loggedUser.value.paymentEdition.name,
      minEdition: "standard",
    })
  ) {
    cb();
  } else {
    // @ts-ignore
    const {
      title = "Upgrade Plan",
      premium_modal_key = "general",
      postModalExec,
    } = modal_options || {};
    UseModal()
      .show(PremiumModal, { key: premium_modal_key })
      .then((res) => {
        if (modal_options) {
          postModalExec && postModalExec();
          if (res.type === "success") {
            UseModal().show(PlansModal);
          }
        }
      });
  }
};

const ifUserEnabledFeature = (
  feature: FeatureId,
  cb: () => void,
  modal_options?: {
    premium_modal_key?: string;
    title?: string;
    postModalExec?: () => void;
  },
) => {
  if (FeatureHelper.is_enabled(feature)) {
    cb();
  } else {
    // @ts-ignore
    const {
      title = "Upgrade Plan",
      premium_modal_key = "general",
      postModalExec,
    } = modal_options || {};
    UseModal()
      .show(PremiumModal, { key: premium_modal_key })
      .then((res) => {
        if (modal_options) {
          postModalExec && postModalExec();
          if (res.type === "success") {
            UseModal().show(PlansModal);
          }
        }
      });
  }
};

const is_not_production = getter(
  (s) => !["stage", "production"].includes(s.env.env),
);

const get_activation_date = (app_name: string) => {
  const activation_date = computed(() => {
    return (
      apps.raw_settings.value[app_name].activation_date ||
      new Date().toISOString()
    );
  });

  const is_pre_activation_date = computed(() => {
    const current = moment(SharedState.selectedTimeFrameOption.value.from);
    const current_year = current.year();
    const current_month = current.month();

    const activation = moment(activation_date.value);
    const activation_year = activation.year();
    const activation_month = activation.month();

    if (current_year > activation_year) {
      return false;
    }
    if (current_year < activation_year) {
      return true;
    }
    return current_month < activation_month;
  });

  return {
    activation_date,
    is_pre_activation_date,
  };
};

const get_org_size = (() => {
  let size = 0;
  return async (): Promise<number> => {
    if (size > 0) {
      return size;
    } else {
      size = await getTotalAccountsCount();
      return size;
    }
  };
})();

export const SharedState = {
  // is_no_other,
  sessions_query_params,
  initial_scan_date: computed(() => basedOn.value.dateFrom),
  ...login,
  ...timeFrame,
  is_data_available,
  ...infotypes,
  ...insights,
  ...apps,
  in_motion_services_flags,
  is_local_dev,
  is_preview,
  is_not_preview,
  is_org_suspended,
  features,
  features_by_group,
  FeatureHelper,
  toggle_feature,
  data_sources,
  data_sources_includes_ms,
  ms_data_sources,
  data_sources_contain_only_gmail,
  user_preferences: {
    list: user_preferences,
    update: update_user_preference,
    remove: remove_user_preference,
  },
  get_org_size,
  get_activation_date,
  useCases: cms.useCases,
  partner: cms.partner,
  pricing_plans: cms.pricing_plans,
  is_already_upgraded: cms.is_already_upgraded,
  premium_features: cms.premium_features,
  glossary: cms.glossary,
  assets: cms.assets,
  info_types_policies_by_threat: cms.info_types_policies_by_threat,
  policies_indexed_by_id: cms.policies_indexed_by_id,
  get_benchmark_grade_by_score: cms.get_benchmark_grade_by_score,
  ifIsPayingUser,
  restrictions: login.restrictions,
  ifUserEnabledFeature,
  isOrgActive,
  isDemo,
  forceInit,
  anonymousUserId,
  onReady,
  before_init_app,
  init,
  env,
  resetPaymentReminderModal,
  isLoadingTimeFrame,
  // user
  paymentEdition,
  // based on
  isLoadingBasedOn,
  basedOn,
  // events
  isLoadingEvent,
  currentEvent,
  theme,
  toggleTheme,
  change_theme_locally,
  is_not_production,
  employees,
  employees_full_objects,
  departments,
  departments_people_count,
  get_employee_department_id,
  get_employee_department_name,
  COGNNI_ADMIN_ID,
  admins,
  admins_with_status,
  // other modules
  investigation: investigation({ is_biz_user: login.is_biz_user }),
};

// @ts-ignore
window.bind_shared_state = () => (window.SharedState = SharedState);
